import { useState, useRef, useEffect } from 'react';
import { KQL_TEMPLATES, HUNT_TEMPLATES } from '../data/demoData';
import { apiFetch } from '../lib/apiClient';
import { useAuth } from '../context/AuthContext';
import { useCustomers } from '../context/CustomerContext';
import { formatInTz, timezoneAbbr } from '../lib/timezones';
import { KqlIntellisenseEditor } from './intelligence/KqlIntellisenseEditor';
import { DEFENDER_XDR_TABLES_FOR_SENTINEL_WORKSPACE } from '../lib/kqlCatalog';
import { AdvancedResultsGrid } from './AdvancedResultsGrid';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface KqlResult {
  columns: string[];
  rows: unknown[][];
  rowCount: number;
  elapsed: string;
}

const SYSTEM_PROMPT = `You are a Microsoft Sentinel SOC analyst AI assistant. 
You help security analysts investigate incidents, understand alerts, write KQL queries, and respond to threats.
Be concise, actionable, and use security domain knowledge. When suggesting KQL, format it in code blocks.
Focus on Microsoft Sentinel, Defender XDR, and Azure security services.`;

async function callAI(messages: Message[], customerId?: string): Promise<{ content: string; live: boolean; error?: string }> {
  try {
    // apiFetch carries the session cookie automatically and surfaces 401s into
    // the global auth listener. We pass customerId through because /ai-chat is
    // tenant-scoped — analysts MUST send one or the scope guard will reject.
    const data = await apiFetch<{ content?: string; error?: string; noConfig?: boolean; noDeployment?: boolean }>('/ai-chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        customerId,
        messages: [
          { role: 'system', content: SYSTEM_PROMPT },
          ...messages.map(m => ({ role: m.role, content: m.content })),
        ],
      }),
    });
    if (data.content) return { content: data.content, live: true };
    return { content: '', live: false, error: data.error };
  } catch (e) {
    return { content: '', live: false, error: e instanceof Error ? e.message : String(e) };
  }
}

const QUICK_PROMPTS = [
  { label: 'Top incidents (7d)', value: 'Show me the top 10 highest severity incidents from the last 7 days' },
  { label: 'Unassigned incidents', value: 'Are there any open incidents that have been unassigned for more than 3 days?' },
  { label: 'Threat summary', value: 'Summarize active threats and which MITRE tactics are most common' },
  { label: 'Suspicious sign-ins', value: 'Are there any suspicious sign-in patterns in the last 24 hours?' },
  { label: 'Brute force check', value: 'Check for any password spray or brute force activity in the last 7 days' },
  { label: 'Top alert IPs', value: 'Show IP addresses involved in the most alerts this week' },
];

function cellDisplay(val: unknown): string {
  if (val === null || val === undefined) return '';
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
}

function formatCell(col: string, val: unknown, tz: string): string {
  if (val === null || val === undefined) return '';
  if (typeof val === 'object') return JSON.stringify(val);
  const s = String(val);
  if (/time|datetime|timestamp/i.test(col)) {
    const normalized = s.replace(/(\.[0-9]{3})[0-9]+/, '$1');
    const d = new Date(normalized);
    if (!isNaN(d.getTime()) && s.includes('T') && s.includes(':')) {
      return formatInTz(d, tz, {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false,
      });
    }
  }
  return s;
}

interface DataLakeTabProps {
  customerId?: string;
  customerName?: string;
  pendingQuery?: string | null;
  onPendingQueryConsumed?: () => void;
}

export function DataLakeTab({ customerId, customerName, pendingQuery, onPendingQueryConsumed }: DataLakeTabProps = {}) {
  const { canCapability } = useAuth();
  const { activeCustomer } = useCustomers();
  const tz = activeCustomer?.timezone ?? 'UTC';
  const canAskAi = canCapability('ai_assistant', 'ask_ai');
  const canRunKql = canCapability('ai_assistant', 'run_generated_kql');
  const [messages, setMessages] = useState<Message[]>([
    { role: 'assistant', content: 'SOC AI Assistant.\n\nThe **KQL Console** below is connected to your Microsoft Sentinel workspace. Type a query and click Run.\n\nChecking AI service connection…' }
  ]);
  const [aiLive, setAiLive] = useState<boolean | null>(null);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [kqlQuery, setKqlQuery] = useState('SecurityIncident\n| where TimeGenerated > ago(7d)\n| summarize count() by Severity\n| order by count_ desc');
  const [kqlResults, setKqlResults] = useState<KqlResult | null>(null);
  const [kqlRunning, setKqlRunning] = useState(false);
  const [kqlMeta, setKqlMeta] = useState('');
  const [kqlError, setKqlError] = useState('');
  const [huntOpen, setHuntOpen] = useState(false);
  const [huntCategory, setHuntCategory] = useState(HUNT_TEMPLATES[0]?.label ?? '');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Check if AI is live on mount. The /config/test-ai probe is admin-only on
  // the server, so analysts will hit a 403 here — treat that as "live" rather
  // than dropping into demo mode, since they shouldn't be told to fix MSSP
  // configuration they can't see.
  useEffect(() => {
    apiFetch<{ ok: boolean; message?: string; noDeployments?: boolean; wrongDeployment?: boolean }>('/config/test-ai', { method: 'POST' })
      .then((d) => {
        setAiLive(d.ok);
        if (d.ok) {
          setMessages([{ role: 'assistant', content: 'SOC AI Assistant connected.\n\nThe **KQL Console** is live against your Sentinel workspace.\n\nAsk a question about your security environment, incidents, or threat intelligence.' }]);
        } else {
          const hint = d.noDeployments
            ? 'No model deployment found. Create one in your AI portal, then update Settings, AI / Foundry.'
            : d.wrongDeployment
            ? `Deployment name mismatch. Update the model name in Settings, AI / Foundry. ${d.message ?? ''}`
            : 'Connect the AI service in Settings, AI / Foundry to enable live AI analysis.';
          setMessages([{ role: 'assistant', content: `SOC AI Assistant.\n\nThe **KQL Console** below is live against Sentinel.\n\nAI chat is in **demo mode**. ${hint}` }]);
        }
      })
      .catch((err: unknown) => {
        // Analysts get 403 on the admin-only probe; assume AI is up for them.
        const isForbidden = err instanceof Error && err.name === 'ApiError' && (err as { status?: number }).status === 403;
        setAiLive(isForbidden ? true : false);
        setMessages([{ role: 'assistant', content: isForbidden
          ? 'SOC AI Assistant connected.\n\nThe **KQL Console** is live against your Sentinel workspace.\n\nAsk a question about your security environment, incidents, or threat intelligence.'
          : 'SOC AI Assistant.\n\nThe **KQL Console** is live against your Sentinel workspace.\n\nAI chat is in **demo mode**. Configure the AI service in Settings, AI / Foundry to enable live analysis.' }]);
      });
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function sendMessage() {
    if (!canAskAi) {
      setMessages(m => [...m, { role: 'assistant', content: 'Permission denied: your role is not allowed to use the AI Assistant chat.' }]);
      return;
    }
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput('');
    const newMessages: Message[] = [...messages, { role: 'user' as const, content: userMsg }];
    setMessages(newMessages);
    setLoading(true);

    if (aiLive) {
      const result = await callAI(newMessages, customerId);
      if (result.live) {
        setMessages(m => [...m, { role: 'assistant', content: result.content }]);
      } else {
        setMessages(m => [...m, { role: 'assistant', content: `AI error: ${result.error ?? 'Unknown error'}. Check Settings, AI / Foundry.` }]);
      }
    } else {
      // Demo fallback
      await new Promise(r => setTimeout(r, 900));
      const q = userMsg.toLowerCase();
      let resp = 'The AI assistant is in demo mode. Configure Azure OpenAI in Settings, AI / Foundry to get real answers.\n\nMeanwhile, use the KQL Console above to query your Sentinel workspace directly.';
      if (q.includes('incident') || q.includes('severity')) resp = 'In live mode, I would query your Sentinel incidents and summarize them. Use the KQL Console with:\n\n`SecurityIncident | where TimeGenerated > ago(7d) | summarize count() by Severity`';
      if (q.includes('brute') || q.includes('password') || q.includes('spray')) resp = 'For brute force analysis, run this in the KQL Console:\n\n`SigninLogs | where ResultType != "0" | summarize Attempts=count() by UserPrincipalName, IPAddress | top 20 by Attempts`';
      setMessages(m => [...m, { role: 'assistant', content: resp }]);
    }
    setLoading(false);
  }

  async function runKql(overrideQuery?: string) {
    const q = (overrideQuery ?? kqlQuery).trim();
    if (!canRunKql) {
      setKqlError('Permission denied: your role is not allowed to run generated KQL from the AI Assistant.');
      return;
    }
    if (!q || kqlRunning) return;
    setKqlRunning(true);
    setKqlMeta('Running against Sentinel…');
    setKqlResults(null);
    setKqlError('');
    try {
      const qs = customerId ? `?customerId=${encodeURIComponent(customerId)}` : '';
      const result = await apiFetch<KqlResult>(`/kql${qs}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: q }),
      });
      setKqlResults(result);
      setKqlMeta(`${result.rowCount} row${result.rowCount !== 1 ? 's' : ''} in ${result.elapsed}s (live)`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setKqlError(msg);
      setKqlMeta('');
    } finally {
      setKqlRunning(false);
    }
  }

  // Match the analyst shortcuts used in Microsoft Sentinel / Log Analytics
  // workbooks: Ctrl+Enter continues a query pipeline on the next line,
  // Shift+Enter runs the query — same convention as Advanced Hunting.
  function handleKqlKeydown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key !== 'Enter') return;

    if (e.ctrlKey && !e.shiftKey && !e.altKey && !e.metaKey) {
      e.preventDefault();
      const textarea = e.currentTarget;
      const start = textarea.selectionStart ?? kqlQuery.length;
      const end = textarea.selectionEnd ?? start;
      const insertion = '\n| ';
      const nextQuery = `${kqlQuery.slice(0, start)}${insertion}${kqlQuery.slice(end)}`;
      setKqlQuery(nextQuery);

      window.setTimeout(() => {
        const caret = start + insertion.length;
        textarea.selectionStart = caret;
        textarea.selectionEnd = caret;
      }, 0);
      return;
    }

    if (e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey) {
      e.preventDefault();
      runKql();
    }
  }

  // ── When a pending query arrives from the AI investigation, pre-fill and auto-run ──
  useEffect(() => {
    if (pendingQuery && pendingQuery.trim()) {
      setKqlQuery(pendingQuery);
      setKqlResults(null);
      setKqlError('');
      runKql(pendingQuery);
      onPendingQueryConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingQuery]);

  function renderMessage(content: string) {
    const parts = content.split(/(\*\*[^*]+\*\*|`[^`]+`|\n)/g);
    return parts.map((p, i) => {
      if (p.startsWith('**') && p.endsWith('**')) return <strong key={i}>{p.slice(2, -2)}</strong>;
      if (p.startsWith('`') && p.endsWith('`')) return <code key={i} className="bg-black/20 rounded px-1 font-mono text-xs">{p.slice(1, -1)}</code>;
      if (p === '\n') return <br key={i} />;
      return p;
    });
  }

  function copyResults() {
    if (!kqlResults) return;
    const header = kqlResults.columns.join('\t');
    const rows = kqlResults.rows.map(r =>
      (r as unknown[]).map((cell, j) => formatCell(kqlResults.columns[j]!, cell, tz)).join('\t')
    ).join('\n');
    navigator.clipboard.writeText(`${header}\n${rows}`).catch(() => null);
  }

  return (
    <div className="slide-in space-y-6">
      <div className="bg-card rounded-xl shadow p-5">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <h3 className="font-semibold text-foreground">KQL Console</h3>
          <span className="text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 px-2 py-0.5 rounded-full font-semibold">● LIVE</span>
          {customerName && (
            <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
              Workspace: {customerName}
            </span>
          )}
          <button
            type="button"
            onClick={() => setHuntOpen(o => !o)}
            className={`text-xs px-2 py-0.5 rounded-full font-semibold border transition-colors ${huntOpen ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted/50 text-muted-foreground border-border hover:bg-muted'}`}
            aria-expanded={huntOpen}
            aria-controls="hunt-templates-panel"
            title="Toggle the curated KQL hunt template library"
          >
            {huntOpen ? '▾ Hunt Templates' : '▸ Hunt Templates'}
          </button>
          <span className="text-xs text-muted-foreground ml-auto">Ctrl+Enter to run</span>
        </div>

        {huntOpen && (
          <div
            id="hunt-templates-panel"
            className="mb-3 border border-border rounded-lg bg-muted/30 p-3"
            role="region"
            aria-label="Hunt templates library"
          >
            <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Hunt Templates by MITRE tactic
              </div>
              <div className="text-[11px] text-muted-foreground">
                Click any template to load it into the editor
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {HUNT_TEMPLATES.map(cat => (
                <button
                  key={cat.label}
                  type="button"
                  onClick={() => setHuntCategory(cat.label)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${huntCategory === cat.label ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-foreground border-border hover:bg-muted'}`}
                  aria-pressed={huntCategory === cat.label}
                >
                  {cat.label}
                  <span className="ml-1 opacity-70">({cat.templates.length})</span>
                </button>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {(HUNT_TEMPLATES.find(c => c.label === huntCategory)?.templates ?? []).map(t => (
                <button
                  key={t.label}
                  type="button"
                  onClick={() => {
                    setKqlQuery(t.query);
                    setKqlResults(null);
                    setKqlError('');
                    setKqlMeta('');
                  }}
                  className="text-left border border-border bg-background rounded-lg p-2.5 hover:border-primary hover:bg-muted/50 transition-colors"
                  title={t.query}
                >
                  <div className="text-xs font-semibold text-foreground mb-1 truncate">{t.label}</div>
                  <div className="text-[11px] text-muted-foreground line-clamp-2">{t.description}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        <KqlIntellisenseEditor
          className="kql-editor mb-2"
          value={kqlQuery}
          onChange={setKqlQuery}
          onKeyDown={handleKqlKeydown}
          source="sentinel"
          customerName={customerName}
          placeholder="Enter KQL query… (Shift+Enter to run)"
          schemaTables={DEFENDER_XDR_TABLES_FOR_SENTINEL_WORKSPACE}
        />
        <div className="flex flex-wrap gap-2 items-center mb-3">
          <button onClick={() => runKql()} disabled={kqlRunning || !canRunKql}
            title={!canRunKql ? 'Your role is not allowed to run KQL from the AI Assistant' : undefined}
            className="bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-semibold hover:opacity-90 disabled:opacity-60 transition-opacity">
            {kqlRunning ? 'Running…' : 'Run Query'}
          </button>
          <button onClick={() => { setKqlQuery(''); setKqlResults(null); setKqlError(''); setKqlMeta(''); }}
            className="border border-border bg-muted/50 text-muted-foreground px-3 py-2 rounded-lg text-sm hover:bg-muted transition-colors">
            Clear
          </button>
          <select onChange={e => { if (e.target.value) { setKqlQuery(e.target.value.replace(/\\n/g, '\n')); setKqlResults(null); setKqlError(''); setKqlMeta(''); e.target.selectedIndex = 0; } }}
            className="border border-border bg-background rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary">
            <option value="">Templates…</option>
            {KQL_TEMPLATES.map(t => <option key={t.label} value={t.value}>{t.label}</option>)}
          </select>
          {kqlMeta && <span className="text-xs text-muted-foreground ml-1">{kqlMeta}</span>}
        </div>

        {kqlError && (
          <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2 mb-3 max-h-32 overflow-y-auto select-text whitespace-pre-wrap break-words font-mono">
            {kqlError}
          </div>
        )}

        {kqlResults && kqlResults.rows.length > 0 && (
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="text-xs text-muted-foreground">{kqlResults.rowCount} row{kqlResults.rowCount !== 1 ? 's' : ''} in {kqlResults.elapsed}s</span>
              <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full font-mono" title={tz}>{timezoneAbbr(tz)}</span>
              <button onClick={copyResults}
                className="text-xs border border-border bg-muted/50 text-muted-foreground px-2 py-1 rounded hover:bg-muted transition-colors ml-auto">
                Copy as TSV
              </button>
            </div>
            <AdvancedResultsGrid
              columns={kqlResults.columns}
              rows={kqlResults.rows}
              storageKey="data-lake-kql-console"
            />
          </div>
        )}

        {kqlResults && kqlResults.rows.length === 0 && (
          <div className="text-sm text-muted-foreground bg-muted/30 rounded-lg px-4 py-3">
            Query returned 0 rows. The table may be empty for the selected time range.
          </div>
        )}
      </div>

      <div className="bg-card rounded-xl shadow p-5">
        <div className="flex items-center gap-2 mb-3">
          <h3 className="font-semibold text-foreground">AI Assistant</h3>
          {aiLive === null && <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full font-semibold">Checking…</span>}
          {aiLive === true && <span className="text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 px-2 py-0.5 rounded-full font-semibold">● LIVE</span>}
          {aiLive === false && <span className="text-xs bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300 px-2 py-0.5 rounded-full font-semibold">DEMO</span>}
        </div>
        <div className="mb-3">
          <select onChange={e => { if (e.target.value) { setInput(e.target.value); e.target.selectedIndex = 0; } }}
            className="w-full border border-border bg-background rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary">
            <option value="">Quick prompts…</option>
            {QUICK_PROMPTS.map(p => <option key={p.label} value={p.value}>{p.label}</option>)}
          </select>
        </div>
        <div className="border border-border rounded-xl p-4 min-h-[300px] max-h-[400px] overflow-y-auto bg-muted/30 mb-3 space-y-3">
          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`${msg.role === 'user' ? 'ai-message-user' : 'ai-message-assistant'} text-sm leading-relaxed max-w-[85%]`}>
                {renderMessage(msg.content)}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="ai-message-assistant text-sm text-muted-foreground">
                <span className="animate-pulse">Analyzing…</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        <div className="flex gap-2">
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && sendMessage()}
            placeholder={canAskAi ? "Ask the AI assistant a security question…" : "AI Assistant chat is not enabled for your role"}
            disabled={!canAskAi}
            className="flex-1 border border-border bg-background rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-60"
          />
          <button onClick={sendMessage} disabled={loading || !canAskAi}
            className="bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-semibold hover:opacity-90 disabled:opacity-60">Send</button>
          <button onClick={() => setMessages([{ role: 'assistant', content: 'Chat cleared. How can I help?' }])}
            className="border border-border bg-muted/50 text-muted-foreground px-3 py-2 rounded-lg text-sm">Clear</button>
        </div>
      </div>
    </div>
  );
}
